import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
const STAGES = [{
  phase: 'Attract',
  label: 'Hook',
  line: 'Earn the tap.',
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8c12d81b-81c9-4950-a9df-8b72bc22f05b.jpg',
  alt: 'Banner ad in a messaging app inviting the HCP into a case'
}, {
  phase: 'Engage',
  label: 'Snak',
  line: 'Earn the interaction.',
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/58369ee1-e4a5-4920-bde3-b70e71b20ceb.jpg',
  alt: 'Snak screen asking the HCP what they would prescribe'
}, {
  phase: 'Learn',
  label: 'Signals',
  line: 'Understand what HCPs think.',
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/e0f59a14-3932-4db7-bd6d-4e3215b287c2.jpg',
  alt: 'Dashboard showing engagement and answer distribution'
}, {
  phase: 'Improve',
  label: 'Next campaign',
  line: 'Use the learning.',
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/f56b4488-53cb-4caa-ae80-a720cbf1c3eb.jpg',
  alt: 'Opening screen of the next Snak campaign'
}] as const;
export function SlideSnakToCampaign({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-180" data-mp="14p4z6313e0i7">Every campaign makes the next one smarter.</SlideBanner>} data-id="element-179" data-mp="14qg5oalx9md0">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-181" data-mp="7z47nw9c95x7">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-182" data-mp="h0lstt1lrxsr">
          From Snak to campaign
        </p>
        <h2 className="max-w-4xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-183" data-mp="3lgwo1b07aw6g">
          One Snak can become a{' '}
          <span className="text-[var(--brand)]" data-id="element-184" data-mp="wn87x2co0o6k">continuous campaign.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.4rem,1vw,1rem)]" data-id="element-185" data-mp="roez3n8bplcq">
        {STAGES.map((stage, index) => <React.Fragment key={stage.phase} data-id="element-186" data-mp="16c8fha58grwd" data-mpi={index}>
            <motion.figure initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.12,
          duration: 0.5
        }} className="flex min-h-0 min-w-0 flex-1 flex-col items-center" data-id="element-187" data-mp="11dez99ykamsi">
              <span className="flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1vw,0.95rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-188" data-mp="mp27kp6ibq3t">
                {stage.phase}
              </span>
              <div className="mt-[clamp(0.5rem,1vw,0.9rem)] flex min-h-0 flex-1 items-center justify-center" data-id="element-189" data-mp="1y6adxg0prgam">
                <img src={stage.src} alt={stage.alt} className="max-h-full w-auto rounded-[1.25rem] object-contain drop-shadow-[0_18px_40px_rgba(23,32,51,0.16)]" data-id="element-190" data-mp="1u4yopblr4x2y" />
              </div>
              <figcaption className="mt-[clamp(0.5rem,1vw,0.9rem)] flex-shrink-0 text-center" data-id="element-191" data-mp="1rp8vo4035wdg">
                <p className="text-[clamp(0.95rem,1.4vw,1.35rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-192" data-mp="1wntlralmizo6">
                  {stage.label}
                </p>
                <p className="mt-0.5 text-[clamp(0.7rem,1vw,0.92rem)] text-[var(--muted)]" data-id="element-193" data-mp="2pbz8g2w8myqn">
                  {stage.line}
                </p>
              </figcaption>
            </motion.figure>

            {index < STAGES.length - 1 && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          x: [0, 5, 0]
        }} transition={{
          opacity: {
            delay: 0.3 + index * 0.12,
            duration: 0.4
          },
          x: {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.3
          }
        }} className="flex h-9 w-9 flex-shrink-0 items-center justify-center self-center rounded-full bg-[var(--brand)] text-white shadow-sm" aria-hidden="true" data-id="element-194" data-mp="2fjtggt5zkhbg">
                <ArrowRightIcon className="h-4 w-4" data-id="element-195" data-mp="234wscmdc8o1h" />
              </motion.span>}
          </React.Fragment>)}
      </div>
    </InteriorSlide>;
}